
    export const projectsData = {
      "CUB-3D": {
        "Project Overview":
          "Cub-3D is a pseudo-3D game engine that simulates a first-person perspective using raycasting. Unlike modern 3D engines that use polygons, Cub-3D traces rays from the player's viewpoint to determine the distance of objects and render the world accordingly. This project explores low-level graphics programming, math-heavy rendering techniques, and performance optimization, making it an excellent way to learn the fundamentals of game development.",
        entro:
          "Cub-3D is a pseudo-3D game engine that simulates a first-person perspective using raycasting. This project is inspired by the world-famous Wolfenstein 3D game",
        "Purpose of the Project":
          "Cub-3D was developed to gain a deeper understanding of low-level graphics programming, computational geometry, and the fundamentals of game rendering. The project serves as an introduction to raycasting, an essential technique used in early 3D games, while also improving problem-solving skills in areas like memory management, math-heavy optimizations, and real-time rendering.",
        Image: "assets/cub_entro.png",
        github: "https://github.com/Yichiba/CUB-3D",

        "Final Render":
          "https://miro.medium.com/v2/resize:fit:1200/1*W2qr42OsK8Zej0NQ2KWk1w.gif",
        "Challenges & Problems Solved": [
          {
            problemTitle: "Map Parsing & Validation",
            Image: "assets/map_validation.png",
            Problem:
              "The game world is defined in a .cub file, which represents a 2D grid map. The challenge is to correctly parse the file while ensuring the map is valid and closed (walls enclose the playable area).",
            "Issues Faced": [
              "Maps could have gaps, leading to memory errors and out-of-bounds raycasting.",
              "Handling different elements (walls, player spawn, empty spaces) was tricky.",
              "Managing file reading errors and invalid syntax required robust error handling.",
            ],
            Solution:
              "Implemented a strict parsing algorithm to read the map and check for correct structure, closed walls, and valid player spawn points. Used flood-fill algorithms to ensure the map is enclosed.",
          },
          {
            problemTitle: "Raycasting & 3D Projection",
            Image: "assets/raycasting.png",
            Problem:
              "The engine needs to simulate a 3D perspective while only having a 2D grid map. The core issue is calculating how far objects are from the player and rendering them accordingly.",
            "Issues Faced": [
              "Traditional 3D engines use polygons, but Cub-3D only has a 2D grid, making depth calculation difficult.",
              "Rays must stop when hitting walls, and finding the exact point of collision was challenging.",
              "Fisheye distortion occurs when rays are cast in a straightforward way.",
            ],
            Solution:
              "Used the DDA (Digital Differential Analyzer) algorithm to efficiently trace rays and detect walls. Calculated wall distances using the Pythagorean theorem, corrected for fisheye distortion, and implemented a field of view (FOV) system by casting multiple rays per frame.",
          },
          {
            problemTitle: "Wall Texturing & Rendering",
            Image: "assets/wall_Texturing.png",
            Problem:
              "Simple color shading made the environment look dull, so adding wall textures was necessary. The challenge was mapping the correct texture pixel to the rendered wall slice.",
            "Issues Faced": [
              "Mapping 2D textures onto raycasted walls required precise UV coordinate calculations.",
              "Walls appeared stretched if texture mapping was incorrect.",
              "Handling multiple wall types (different textures for different map sections).",
            ],
            Solution:
              "Used the point of intersection between the ray and the wall to determine which pixel of the texture to use. Normalized texture coordinates by dividing the intersection point by texture size and implemented different textures for different wall types.",
          },
        ],

        TechStack: [
          "C Language - High performance and memory control.",
          "MiniLibX - Lightweight graphics library for rendering.",
          "Math  - For raycasting and movement calculations.",
          "DDA Algorithm - For efficient raycasting.",
        ],
        "Final Thoughts":
          "Cub-3D was a challenging yet rewarding project that taught essential game development skills like raycasting, texture mapping, and collision handling. The biggest hurdles were math-heavy calculations, optimizing performance, and ensuring smooth player interaction.",
      },
      Inception: {
        "Project Overview":
          "Inception is a system administration project focused on containerization using Docker and Docker Compose. It involves setting up a virtualized environment with multiple services running in isolated containers, ensuring modularity, scalability, and security. The project follows the principle of 'Infrastructure as Code,' automating deployment through configuration files rather than manual installations.",
        entro:
          "Inception is a system administration project focused on containerization using Docker and Docker Compose.",
        "Purpose of the Project":
          "The goal of Inception is to understand virtualization vs. containerization, learn networking in Docker, and explore service orchestration. It enforces best practices in system administration, security policies, and resource management within a Docker-based ecosystem.",
        Image: "assets/inception_final_render.png",
        github: "https://github.com/Yichiba/vm-inception",
        "Final Render": "assets/Mems.jpg",
        "Challenges & Problems Solved": [
          {
            problemTitle: "Containerizing Services & Isolation",
            Image: "assets/architecture.png",
            Problem:
              "Unlike traditional installations where services run on the host OS, Docker encapsulates each service in isolated containers, avoiding conflicts but requiring careful configuration.",
            "Issues Faced": [
              "Ensuring correct inter-container communication (networking).",
              "Managing volumes to persist data after container restarts.",
              "Configuring environment variables and secrets securely.",
            ],
            Solution: [
              "Defined custom Docker networks to allow communication between specific containers.",
              "Used Docker volumes to persist MySQL, WordPress, and Nginx data.",
              "Implemented .env files for secret management, keeping configurations dynamic.",
            ],
          },
          {
            problemTitle: "WordPress & MySQL Setup with Persistent Data",
            Problem:
              "WordPress requires MySQL for database management, but ensuring data persists after container restarts was a challenge.",
            "Issues Faced": [
              "Containers were being recreated without saving user data.",
              "WordPress needed to dynamically connect to MySQL, avoiding hardcoded credentials.",
              "Ensuring MySQL is ready before WordPress initializes.",
            ],
            Solution: [
              "Used Docker volumes to persist database data.",
              "Configured WordPress to read database credentials from environment variables.",
              "Used health checks to delay WordPress startup until MySQL was fully initialized.",
            ],
          },
          {
            problemTitle: "Multi-Container Orchestration with Docker Compose",
            Image: "assets/Multi-Container_Orchestration.png",
            Problem:
              "Managing multiple containers separately is inefficient. Docker Compose allows defining all services in a single docker-compose.yml file, but ensuring correct startup order and dependencies was tricky.",
            "Issues Faced": [
              "Services starting before their dependencies were ready (e.g., WordPress starting before MySQL was initialized).",
              "Configuring correct resource allocation (memory, CPU limits) for each container.",
              "Handling container restarts after failure.",
            ],
            Solution: [
              "Used depends_on in docker-compose.yml to control the startup sequence.",
              "Defined restart policies to automatically restart failed containers.",
              "Set CPU/memory limits in the docker-compose file to optimize resource usage.",
            ],
          },
          {
            problemTitle: "System Security & User Management",
            Problem:
              "Running services inside Docker doesn't automatically secure them. Additional security policies were needed to prevent unauthorized access.",
            "Issues Faced": [
              "Preventing root access inside containers.",
              "Restricting public exposure of services.",
              "Enforcing secure passwords & authentication methods.",
            ],
            Solution: [
              "Ran services with non-root users inside containers for security.",
              "Restricted access to internal networks, exposing only necessary ports.",
              "Configured secure MySQL passwords and WordPress admin credentials using .env variables.",
            ],
          },
        ],
        TechStack: [
          "Docker ",
          " Docker Compose",
          "Nginx",
          "MySQL",
          "WordPress",
          "OpenSSL",
          "Linux & Shell Scripting",
        ],
        "Final Thoughts":
          "Inception provides real-world experience in containerization, networking, and secure server administration. The project’s biggest takeaways were understanding how to manage dependencies between services, configuring a secure system, and ensuring persistence in a containerized environment.",
      },
      ft_transcendence: {
        "Project Overview":
          "ft_transcendence is a full-stack web application that implements a real-time multiplayer Pong game with user authentication, matchmaking, chat, and social features. This project was a collaborative effort with two teammates, where I took full responsibility for the user management . The project combines frontend and backend technologies to create a seamless user experience while focusing on security, scalability, and interactivity.",
        entro:
          "ft_transcendence is a full-stack web application that implements a real-time multiplayer Pong game with user authentication, matchmaking, chat, and social features.",
        Image: "assets/final_render.png",
        "Final Render": "assets/final_render.png",
        "Purpose of the Project":
          "The purpose of ft_transcendence is to provide students with a deep understanding of full-stack development, real-time communication using WebSockets, and secure authentication systems. The project challenges students to implement advanced features such as two-factor authentication (2FA), friend systems, and database management while maintaining a smooth user experience.",

        "Challenges & Problems Solved": [
          {
            problemTitle: "Real-time Gameplay with WebSockets",
            Image: "",
            Problem:
              "Implementing real-time interactions for a multiplayer game requires a highly responsive system where players' actions are synchronized instantly across different clients.",
            "Issues Faced": [
              "Latency issues affecting game responsiveness.",
              "Handling synchronization of player movements without glitches.",
              "Ensuring smooth user experience even with network fluctuations.",
            ],
            Solution:
              "Used WebSockets via Socket.IO to establish real-time bidirectional communication between clients and the server. Implemented lag compensation techniques and optimized event handling to minimize latency.",
          },
          {
            problemTitle: "OAuth2 and Two-Factor Authentication (2FA)",
            Image: "assets/OTP.png",
            Problem:
              "The application requires a secure authentication system that supports OAuth2 login (e.g., via Google or 42 intra) and an additional layer of security through Two-Factor Authentication (2FA).",
            "Issues Faced": [
              "OAuth2 integration with multiple providers.",
              "Generating and verifying 2FA codes securely.",
              "Managing user sessions and tokens correctly.",
            ],
            Solution:
              "Integrated OAuth2 authentication for login and used Time-based One-Time Passwords (TOTP) for 2FA. The backend securely stored user authentication data and ensured encrypted verification.",
          },
          {
            problemTitle: "Matchmaking and Elo Ranking System",
            Image: "",
            Problem:
              "Implementing a ranking and matchmaking system where players are matched fairly based on skill level.",
            "Issues Faced": [
              "Developing a matchmaking algorithm that balances player skills.",
              "Ensuring users are matched within reasonable wait times.",
              "Maintaining fairness in ranking calculations.",
            ],
            Solution:
              "Implemented an Elo-based ranking system where player ratings adjust dynamically based on match outcomes. Matchmaking pairs users with similar Elo ratings to ensure balanced gameplay.",
          },
          {
            problemTitle: "User Management & Authentication",
            Image: "assets/user_management.png",
            Problem:
              "Ensuring secure user authentication and management with JWT, email verification, and OTP-based security.  This was my primary area of focus within the project.",
            "Issues Faced": [
              "Handling user registration and login securely.",
              "Implementing JWT-based authentication and refresh token handling.",
              "Verifying user email before account activation.",
              "Adding OTP-based login for additional security.",
              "Remote login functionality via Intra (42 platform).",
              "Ensuring smooth user data updates (e.g., username, profile settings).",
            ],
            Solution:
              "Implemented a robust authentication system using JWT tokens for session management. Integrated email verification via unique tokens sent to the user's email upon registration. Enabled OTP-based authentication for enhanced security. Added remote login capabilities using intra authentication APIs and improved user profile update mechanisms.",
          },
          {
            problemTitle: "Friend System & Social Features",
            Image: "assets/friend_system.png",
            Problem:
              "Creating a friend system that allows users to add, remove, and interact with friends within the platform.",
            "Issues Faced": [
              "Managing friend requests and confirmations securely.",
              "Implementing real-time notifications for friend interactions.",
              "Ensuring database consistency and preventing duplicate friend entries.",
            ],
            Solution:
              "Developed a friend system where users can send, accept, and reject friend requests. Implemented WebSocket-based notifications for real-time updates and ensured database integrity to prevent duplicate relationships.",
          },
        ],

        TechStack: [
          "Django - Backend framework for handling authentication, WebSockets, and database interactions.",
          "Vanilla JavaScript - Frontend for creating a responsive and interactive UI.",
          "PostgreSQL - Relational database for storing user data, match history, and rankings.",
          "Socket.IO - Enables real-time communication between clients.",
          "Docker - Used for containerized deployment and consistency.",
          "JWT - JSON Web Token-based authentication for user sessions.",
          "Nodemailer - Email service for account verification and notifications.",
        ],
        github: "https://github.com/Yichiba/ft_trancendence_Backend",

        "Final Thoughts":
          "ft_transcendence was an extensive project that provided valuable experience in full-stack development, security practices, and real-time communication. Working with two teammates allowed for a focused approach, with my contributions centered on the complexities of user management backend development. The biggest challenges were optimizing real-time interactions, implementing secure authentication, and ensuring seamless user management. The project demonstrated the importance of structured backend logic and efficient data handling.  Implementing a robust friend system and improving user data updates were also critical in enhancing the overall user experience.",
      },
      WebServer: {
        "Project Overview":
          "WebServer is a custom-built HTTP server that processes web requests, serves static files, and manages HTTP responses. Unlike pre-built web servers like Nginx or Apache, this project dives deep into low-level networking, request parsing, and server optimization using raw sockets. It provides a fundamental understanding of how web servers handle HTTP requests and deliver content efficiently.",
        entro:
          "WebServer is a custom-built HTTP server that processes web requests, serves static files, and manages HTTP responses.",
        "Final Render": "assets/webserver_final_render.png",
        Image: "assets/webserver_final_render.png",

        "Purpose of the Project":
          "The primary goal of WebServer is to gain hands-on experience with low-level networking, socket programming, and HTTP protocol handling. The project aims to build a scalable, efficient web server capable of handling concurrent client requests, parsing HTTP messages, and serving static or dynamic content.",

        "Challenges & Problems Solved": [
          {
            problemTitle: "Socket Programming & Connection Handling",
            Image: "assets/socket_programing.png",
            Problem:
              "The server must listen for incoming client connections and handle multiple requests simultaneously without blocking operations.",
            "Issues Faced": [
              "Creating and managing raw sockets manually.",
              "Handling multiple clients at once without slowing down response times.",
              "Preventing blocking operations, which could freeze the server.",
            ],
            Solution:
              "Used socket programming (socket(), bind(), listen(), accept()) to establish a server-client connection. Implemented non-blocking I/O with select() or poll() to handle multiple clients simultaneously. Used multi-threading or event-driven models to optimize request handling.",
          },
          {
            problemTitle: "Parsing HTTP Requests",
            Problem:
              "The server must correctly interpret HTTP requests from clients (GET, POST, etc.) and extract relevant information such as headers and request bodies.",
            "Issues Faced": [
              "Extracting method, headers, and body from raw HTTP requests.",
              "Handling incomplete or malformed requests gracefully.",
              "Supporting different content types (text/html, application/json, image/png, etc.).",
            ],
            Solution:
              "Built a custom request parser to break down raw HTTP messages into structured data. Implemented error handling to return appropriate HTTP status codes (400 Bad Request, etc.). Allowed MIME-type detection to serve different file types correctly.",
          },
          {
            problemTitle: "Serving Static Files (HTML, CSS, JS, Images)",
            Problem:
              "The server must efficiently read and serve static files from disk (e.g., index.html, style.css).",
            "Issues Faced": [
              "Reading large files efficiently without excessive memory usage.",
              "Handling non-existent files properly (404 Not Found).",
              "Managing content-type headers for different file formats.",
            ],
            Solution:
              "Used buffered file reading to serve content efficiently (read() and send() for chunks). Implemented a filesystem check to verify if the requested file exists. Sent correct MIME types based on file extensions (e.g., Content-Type: text/css).",
          },
          {
            problemTitle: "Implementing HTTP Response Handling",
            Image: "assets/http_response.png",
            Problem:
              "The server must construct valid HTTP responses with the correct status codes, headers, and body content.",
            "Issues Faced": [
              "Formatting HTTP response headers correctly (HTTP/1.1 200 OK).",
              "Managing chunked responses for large files.",
              "Ensuring fast response times with minimal overhead.",
            ],
            Solution:
              "Created a response builder that dynamically generates headers and body content. Implemented gzip compression for faster response delivery. Optimized memory usage by sending data in chunks instead of loading everything into RAM.",
          },
          {
            problemTitle: "Handling Concurrent Connections",
            Problem:
              "A single-threaded server cannot efficiently serve multiple clients at once.",
            "Issues Faced": [
              "If multiple clients request files simultaneously, the server could block or crash.",
              "High latency and response delays due to sequential request handling.",
              "Risk of denial-of-service (DoS) attacks if too many connections are open.",
            ],
            Solution:
              "Used multi-threading (pthreads) or event-driven models (epoll, select) to handle concurrent requests. Implemented timeouts and request limits to prevent server overload. Designed a connection queue to manage incoming clients efficiently.",
          },
        ],

        TechStack: [
          "C Language - For low-level memory management and performance.",
          "Socket Programming (Berkeley Sockets API) - For network communication.",
          "Multi-threading (pthreads) & Event Loop (select/poll/epoll) - For concurrency.",
          "File I/O (read/write) - For serving static files.",
          "HTTP Protocol Handling - Parsing requests and generating responses.",
        ],
        github: "https://github.com/Yichiba/webserver",

        Images: {
          "Socket Programming": "assets/socket_programming.png",
          "HTTP Parsing": "assets/http_parsing.png",
          "Static File Serving": "assets/static_files.png",
          "Concurrent Connections": "assets/concurrent_connections.png",
        },

        "Final Thoughts":
          "WebServer was an eye-opening project that required deep knowledge of networking, operating systems, and low-level programming. The biggest challenges were efficient request handling, performance tuning, and implementing HTTP parsing from scratch. It provided hands-on experience in optimizing server performance and understanding web protocols at a fundamental level.",
      },
    };
